import type { Cdr } from "@/lib/types";

/**
 * How a leg sounded, from what the switch measured on it. Pure, for `test/call-quality.test.ts`.
 *
 * Two facts about this data decide the whole shape of the mark, and both are easy to get wrong:
 *
 * 1. **MOS tops out at 4.5, not 5.** 4.5 is the ceiling for the codecs this switch negotiates
 *    (`opus` on a browser leg, `PCMU` on a carrier one), so a meter scaled to 5 would draw every
 *    healthy call at 90% and there would be no such thing as a clean call on screen.
 * 2. **No score is not a bad score.** A third of the table carried no RTP at all — a leg that
 *    never answered, a carrier leg the switch measured nothing on — and an empty four-rung meter
 *    for those reads as "this sounded terrible". They get an absence instead.
 */

/** The best score the codecs here produce. The meter and the hover card scale to it. */
export const MOS_CEILING = 4.5;

/**
 * The four bands, and why there are four rather than ITU's five.
 *
 * ITU splits the bottom into `poor` (3.1-3.6, many listeners dissatisfied) and `bad` (below 3.1,
 * nearly all). They are the same finding to whoever is scanning the report — go and look at this
 * call — so the mark folds them together and the hover card gives the exact score.
 */
export type QualityBand = "excellent" | "good" | "fair" | "poor";

export const QUALITY_BANDS: {
  band: QualityBand;
  label: string;
  /** Inclusive lower bound. The band above's bound is this one's exclusive upper. */
  from: number;
  /** How many of the four rungs are lit. Height carries the level where colour cannot. */
  rungs: 1 | 2 | 3 | 4;
  said: string;
}[] = [
  { band: "excellent", label: "Excellent", from: 4.3, rungs: 4,
    said: "Nobody noticed anything." },
  { band: "good", label: "Good", from: 4.0, rungs: 3,
    said: "Slightly rough in places." },
  { band: "fair", label: "Fair", from: 3.6, rungs: 2,
    said: "Audible trouble on the line." },
  { band: "poor", label: "Poor", from: 0, rungs: 1,
    said: "Words were lost." },
];

/** The band a score falls in, or null when the switch measured no audio on this leg. */
export function qualityBand(mos: number | null | undefined): QualityBand | null {
  if (mos === null || mos === undefined || !Number.isFinite(mos)) return null;
  // The last band's bound is 0, so a finite score always matches one.
  return QUALITY_BANDS.find((b) => mos >= b.from)?.band ?? "poor";
}

export function bandDetail(band: QualityBand): (typeof QUALITY_BANDS)[number] {
  return QUALITY_BANDS.find((b) => b.band === band) ?? QUALITY_BANDS[QUALITY_BANDS.length - 1]!;
}

/**
 * How much of the meter to fill.
 *
 * The switch's own `audio_in_quality_percentage` when it sent one, because that is its reading of
 * the same thing and it is already a percentage. Otherwise the score against the ceiling, so the
 * meter still says something on a leg that carried a MOS and nothing else.
 */
export function qualityFill(call: Pick<Cdr, "audio_in_mos" | "audio_in_quality_percentage">): number | null {
  const { audio_in_quality_percentage: pct, audio_in_mos: mos } = call;
  if (pct !== null && pct !== undefined && Number.isFinite(pct)) {
    return Math.max(0, Math.min(100, pct));
  }
  if (mos === null || mos === undefined || !Number.isFinite(mos)) return null;
  return Math.max(0, Math.min(100, (mos / MOS_CEILING) * 100));
}

/**
 * Packets lost as a share of the packets that should have arrived.
 *
 * **The denominator is received + lost, not received.** `audio_in_skip_packet_count` is the jitter
 * buffer's count of slots it never filled, which is not a subset of what arrived: a leg whose
 * buffer starved reports far more skipped than received, and dividing by received alone produced
 * "1204.9% lost" on a real local leg (15,013 skipped against 1,246 received). The share of
 * *expected* packets is bounded, and it tracks the switch's own quality percentage — the worst leg
 * on AWS reads 32% lost against a quality reading of 34%.
 *
 * Null rather than zero when nothing was counted: "0% lost" on a leg that received no packets is
 * a claim about a call that never carried audio.
 */
export function packetLoss(
  call: Pick<Cdr, "audio_in_packet_count" | "audio_in_skip_packet_count">,
): { percent: number; lost: number; expected: number } | null {
  const received = call.audio_in_packet_count;
  const lost = call.audio_in_skip_packet_count;
  if (received === null || received === undefined || lost === null || lost === undefined) return null;
  const expected = received + lost;
  if (expected <= 0) return null;
  return { percent: (lost / expected) * 100, lost, expected };
}

/** How alarming a number is, for the one stat that is at fault rather than the whole card. */
export type StatTone = "normal" | "warn" | "bad";

/** Loss thresholds are the ones telephony uses: 1% audible, 5% words going missing. */
export function packetLossTone(percent: number): StatTone {
  if (percent >= 5) return "bad";
  if (percent >= 1) return "warn";
  return "normal";
}

/**
 * The switch's jitter rates, which are not percentages and do not share the loss thresholds.
 * Read off the real range on this platform: a healthy leg sits at 0.000 and the worst call
 * measured 0.600.
 */
export function jitterLossTone(rate: number): StatTone {
  if (rate >= 0.2) return "bad";
  if (rate >= 0.05) return "warn";
  return "normal";
}

/** One of the four counts behind a score, as the hover card and the call dialog both print it. */
export interface QualityFigure {
  label: string;
  /** Null when the switch did not count it: shown as an absence, never as a zero. */
  value: string | null;
  /** The raw counts a percentage was made from, for the reader who wants to check it. */
  hint?: string;
  tone: StatTone;
}

/**
 * The four counts that explain a score, in the order they are read: loss first because it is
 * the one that is usually at fault, then the two jitter rates, then the totals. Each carries
 * its own tone so a card can name the fault rather than only restate the score. One list for
 * both surfaces, so the dialog and the hover cannot come to disagree about a figure.
 */
export function qualityFigures(
  call: Pick<
    Cdr,
    | "audio_in_packet_count"
    | "audio_in_skip_packet_count"
    | "audio_in_jitter_loss_rate"
    | "audio_in_jitter_burst_rate"
    | "audio_out_packet_count"
  >,
): QualityFigure[] {
  const loss = packetLoss(call);
  const jitter = call.audio_in_jitter_loss_rate;
  const sent = call.audio_out_packet_count;
  const received = call.audio_in_packet_count;
  return [
    {
      label: "Packets lost",
      value: loss ? `${loss.percent.toFixed(1)}%` : null,
      hint: loss ? `${loss.lost.toLocaleString()} of ${loss.expected.toLocaleString()}` : undefined,
      tone: loss ? packetLossTone(loss.percent) : "normal",
    },
    {
      label: "Jitter loss",
      value: jitter === null || jitter === undefined ? null : jitter.toFixed(3),
      tone: jitter === null || jitter === undefined ? "normal" : jitterLossTone(jitter),
    },
    {
      label: "Jitter burst",
      value: call.audio_in_jitter_burst_rate?.toFixed(2) ?? null,
      tone: "normal",
    },
    {
      label: "Sent / received",
      value:
        sent === null || sent === undefined || received === null || received === undefined
          ? null
          : `${sent.toLocaleString()} / ${received.toLocaleString()}`,
      tone: "normal",
    },
  ];
}

/** Whether a leg has enough audio detail for the hover card to be worth opening. */
export function hasAudioDetail(call: Cdr): boolean {
  return (
    call.audio_in_quality_percentage !== null ||
    call.audio_in_packet_count !== null ||
    call.audio_in_jitter_loss_rate !== null ||
    call.read_codec !== null
  );
}

/** `opus` at 48000 -> `opus 48 kHz`. The rate is what tells a browser leg from a carrier one. */
export function codecText(codec: string | null, rate: number | null): string | null {
  if (!codec) return null;
  return rate ? `${codec} ${Math.round(rate / 1000)} kHz` : codec;
}
